import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Landing from "@/pages/Landing";
import PlatformOverview from "@/pages/PlatformOverview";
import ProductPage from "@/pages/ProductPage";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MetalDashboard from "@/pages/MetalDashboard";
import SymbolDashboard from "@/pages/SymbolDashboard";
import OperationsPage from "@/pages/OperationsPage";
import { AuthProvider } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";

const lenisRef = { current: null };

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Smooth in-page anchor navigation via Lenis
    const onClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const id = anchor.getAttribute("href");
      if (!id || id === "#") return;
      const target = document.querySelector(id);
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);
  return null;
}

// Handles scroll position on route changes and hash navigation across pages
function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const lenis = lenisRef.current;
    if (hash) {
      // wait for target to mount, then scroll
      const t = setTimeout(() => {
        const target = document.querySelector(hash);
        if (target) {
          if (lenis) lenis.scrollTo(target, { offset: -80 });
          else target.scrollIntoView();
        }
      }, 60);
      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <div className="App noise-overlay">
      <BrowserRouter>
        <AuthProvider>
          <SmoothScroll />
          <ScrollManager />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/plattform" element={<PlatformOverview />} />
            <Route path="/plattform/:slug" element={<ProductPage />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/metallkurse"
              element={
                <ProtectedRoute>
                  <MetalDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/kurse"
              element={
                <ProtectedRoute>
                  <MetalDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/symbole"
              element={
                <ProtectedRoute>
                  <SymbolDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/konten"
              element={
                <ProtectedRoute>
                  <OperationsPage category="accounts" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/zahlungen"
              element={
                <ProtectedRoute>
                  <OperationsPage category="payments" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/kunden"
              element={
                <ProtectedRoute>
                  <OperationsPage category="customers" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/unternehmen"
              element={
                <ProtectedRoute>
                  <OperationsPage category="company" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/einstellungen"
              element={
                <ProtectedRoute>
                  <OperationsPage category="settings" />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
